/**
 * Money is stored in whole cents throughout the simulation.
 *
 * Floating-point dollars accumulate error over thousands of simulated
 * transactions, which would make the ledger-balance invariant in the tests
 * flaky and the balance harness untrustworthy. Integers remove the problem
 * entirely; formatting back to dollars happens only at the UI edge.
 */
export type Cents = number;

export const dollars = (amount: number): Cents => Math.round(amount * 100);
export const toDollars = (cents: Cents): number => cents / 100;

export function formatMoney(cents: Cents, opts: { sign?: boolean } = {}): string {
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100);
  const frac = (abs % 100).toString().padStart(2, '0');
  const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const prefix = negative ? '-' : opts.sign ? '+' : '';
  return `${prefix}$${grouped}.${frac}`;
}

/** Compact form for HUD use: $1.2k, $10.4k. */
export function formatMoneyShort(cents: Cents): string {
  const abs = Math.abs(cents);
  const sign = cents < 0 ? '-' : '';
  if (abs >= 100_000_00) return `${sign}$${Math.round(abs / 100_000) / 10}M`;
  if (abs >= 10_000_00) return `${sign}$${Math.round(abs / 100_00) / 10}k`;
  return formatMoney(cents);
}
